import { FC, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash';

import { ConfigurationsValues, MappingsContext } from '@app/contexts';
import {
  queryKeys,
  useFeatureFlag,
  usePatronSession,
  useSavedInstancesCompact,
} from '@app/hooks';
import {
  getOpacConfigurationsQuery,
  makeRequest,
  OpacConfig,
  parseJSON,
} from '@app/utils';
import { Feature, ItemStatusDto, loginMethods } from '@app/constants';

const REQUIRED_CONFIGS: OpacConfig[] = [
  {
    module: 'OPAC_TOOLS',
    configName: 'ENABLE_STACKMAP',
    code: 'ENABLE_STACKMAP',
    getConfigValue: config => ({ isStackMapEnabled: config?.value === 'true' }),
  },
  {
    module: 'OPAC_CONFIGURATION',
    configName: 'opac_auth_option',
    code: 'OPAC_AUTH_OPTION',
    getConfigValue: config => {
      const loginMethod = config?.value;

      return {
        isSSOLogin: loginMethod === loginMethods.sso,
        isNativeLogin: loginMethod === loginMethods.native,
      };
    },
  },
  {
    module: 'OPAC_TOOLS',
    configName: 'library_location_map',
    code: 'LIBRARY_LOCATION_MAP',
    getConfigValue: config => ({
      libraryLocationMap: parseJSON(config?.value, {}),
    }),
  },
];

export const MappingsProvider: FC = ({ children }) => {
  const { externalSystemId } = usePatronSession();
  const { isActive: isHoldsAndCheckoutsFlagActive } = useFeatureFlag({
    name: Feature.holdsAndCheckouts,
    isActiveIfNotFound: true,
  });

  const createQueryKey = useCallback(
    key => [key, externalSystemId],
    [externalSystemId]
  );

  const userItemStatusesQuery = useQuery(
    createQueryKey(queryKeys.userItemStatuses),
    () =>
      makeRequest<ItemStatusDto>({
        url: `/opac-patron/account/${externalSystemId}`,
        params: {
          includeHolds: true,
          includeCharges: true,
          includeLoans: true,
        },
      }),
    { enabled: !isEmpty(externalSystemId) && isHoldsAndCheckoutsFlagActive }
  );

  const configurationsQuery = useQuery(createQueryKey('configurations'), () =>
    makeRequest({
      url: '/opac-configurations/entries',
      params: {
        query: getOpacConfigurationsQuery(REQUIRED_CONFIGS),
      },
    }).then(({ configs }) =>
      REQUIRED_CONFIGS.reduce((configsValues, { code, getConfigValue }) => {
        const currentConfig = configs?.find(config => config.code === code);

        return Object.assign(configsValues, getConfigValue(currentConfig));
      }, {})
    )
  );

  const value = useMemo(
    () => ({
      userItemStatuses: userItemStatusesQuery.data,
      areUserItemStatusesLoading: userItemStatusesQuery.isFetching,
      configurations: {
        ...(configurationsQuery.data as ConfigurationsValues),
        configurationsQuery,
      },
    }),
    [
      userItemStatusesQuery.data,
      userItemStatusesQuery.isFetching,
      configurationsQuery,
    ]
  );

  // TODO: Fetch compact saved instances list only once on page load
  useSavedInstancesCompact();

  return (
    <MappingsContext.Provider value={value}>
      {children}
    </MappingsContext.Provider>
  );
};
